import notHandledHere from './notHandledHere';
import setDefaultValuesIfNecessary from '../../../../utilities/setDefaultValuesIfNecessary';
import { metadata } from 'lib_ui-services';
import { errors as _errors } from 'lib_ui-primitives';
import lodash from 'lodash';
const { cloneDeep } = lodash;

const _p = { setDefaultValuesIfNecessary, findHNode: metadata.findHNode };
export const _private = _p;

export default {
    verb: 'doingValidate',
    excludedNamespaceRelations: notHandledHere,
    prerequisites: [],
    priority: -5, // after the regular validation
    description: 'Refuse to submit a transaction that would apply no changes to its entries',
    //this is the actual logic:
    logic
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * A transaction that lists entries but carries no change-set writes nothing, yet every layer
 * downstream reports success: `doingCreate_namespace_relation_transaction` dispatches an update per
 * entry with `{...oldRecord}` (a no-op), no PATCH is produced, and `didCommit` toasts
 * `entries.length` "Items updated". That is silent data loss, and it is reachable without the user
 * doing anything wrong -- Mass Update's dropdowns repopulate themselves from the previous session
 * and a restored value is displayed but not staged, so a form that looks complete can submit
 * nothing (GH #1521).
 *
 * setDefaultValuesIfNecessary now stages those restored values, so this is the backstop rather than
 * the fix. It still earns its place: the same empty change-set arrives when a settling background
 * sync re-initialises the form out from under a selection the user *did* make, which no amount of
 * default-restoring covers.
 *
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 */
async function logic({ data, context }) {
    const { hNode, relation } = context;
    // Deliberately NOT declared as `type: 'transaction'`. The create half of this workflow is
    // published with the active record's type, but `validate` is published with
    // `originatingEventContextType ?? machineContextType` (formMachine/actions.js), so the type a
    // validate carries is not guaranteed to be the one willCreate sees. Match every doingValidate
    // and scope here instead, the way the generic doingValidate_namespace_relation does.
    if (!relation?.endsWith('-transaction')) return;
    // Only a form built to collect a change-set can have an empty one by mistake. A Transaction
    // block with no DynamicFieldEntry exists to record its entries and nothing else, and an empty
    // change-set there is by design -- staying out of the way of those is why this is scoped on the
    // metadata rather than on the namespace/relation alone.
    if (hNode == null || _p.findHNode(hNode.children ?? [], 'DynamicFieldEntry') == null) return;

    const newRecord = cloneDeep(data.newRecord);
    // Apply defaults before measuring, exactly as willCreate_namespace_relation does. Otherwise a
    // value restored by DefaultToPreviousSelection -- which is only merged onto the record by
    // setDefaultValuesIfNecessary -- would read as "no change" here and the save would be blocked
    // for a form that is in fact going to apply something.
    await _p.setDefaultValuesIfNecessary(context, newRecord);
    // The same subtraction willCreate_namespace_relation_transaction uses to derive `changes`.
    // eslint-disable-next-line no-unused-vars
    const { $loki, entries, meta, title, _id, ...changes } = newRecord;

    if (entries?.length > 0 && Object.keys(changes).length === 0) {
        throw new _errors.ValidationError(
            'Nothing was updated.  Select a value to update, then save again.',
            {}
        );
    }
}
